import React, { useRef, useState } from "react";
import { api } from "../lib/config";
import { toast } from "sonner";
import { Upload, X, Loader2, Link as LinkIcon } from "lucide-react";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url; // relative /api/files/... URL
}

/** Single image uploader. value = url string, onChange(url) */
export function ImageUploader({ value, onChange, label = "Image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed — try a smaller image");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted mb-2 flex items-center justify-between">
        <span>{label}</span>
        <button type="button" onClick={() => setShowUrl(!showUrl)} className="inline-flex items-center gap-1 hover:text-owt-terracotta"><LinkIcon className="w-3 h-3"/> {showUrl ? "hide url" : "or paste url"}</button>
      </div>
      <div className="flex items-start gap-3">
        <div className="relative w-28 h-28 shrink-0 border border-owt-border bg-owt-bg overflow-hidden flex items-center justify-center">
          {value ? (
            <>
              <img src={value} alt="" className="w-full h-full object-cover"/>
              <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 bg-owt-forest text-owt-surface p-1" data-testid="image-remove"><X className="w-3 h-3"/></button>
            </>
          ) : (
            <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-owt-muted text-center px-2">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="image-upload-btn"
            className="inline-flex items-center gap-2 bg-owt-forest text-owt-surface px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-owt-terracotta transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
            {busy ? "Uploading" : "Upload image"}
          </button>
          {showUrl && (
            <input
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://... (external URL)"
              className="w-full border border-owt-border px-3 py-2 text-sm bg-owt-surface"
            />
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden"/>
      </div>
    </div>
  );
}

/** Multi image uploader. value = array of urls, onChange(array) */
export function MultiImageUploader({ value = [], onChange, label = "Photos" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(value) ? value : [];

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      onChange([...list, ...urls]);
      toast.success(`${urls.length} image(s) uploaded`);
    } catch {
      toast.error("Some uploads failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => onChange(list.filter((_, x) => x !== i));

  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {list.map((src, i) => (
          <div key={src || i} className="relative w-20 h-20 border border-owt-border overflow-hidden bg-owt-bg">
            <img src={src} alt="" className="w-full h-full object-cover"/>
            <button type="button" onClick={() => removeAt(i)} data-testid={`multi-remove-${i}`} className="absolute top-0.5 right-0.5 bg-owt-forest text-owt-surface p-0.5"><X className="w-3 h-3"/></button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="multi-upload-btn"
          className="w-20 h-20 border border-dashed border-owt-border flex flex-col items-center justify-center gap-1 text-owt-muted hover:border-owt-terracotta hover:text-owt-terracotta transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
          <span className="font-mono text-[8px] tracking-[0.15em] uppercase">Add</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={pick} className="hidden"/>
    </div>
  );
}
